import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import {
  ClrModalModule,
  ClrSelectModule,
  ClrFormsModule,
  ClrDatagridModule,
  ClrSpinnerModule,
  ClrTooltipModule,
} from '@clr/angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { CoreShapeComponent, NgKonvaEventObject, StageComponent } from 'ng2-konva';
import { ImageConfig } from 'konva/lib/shapes/Image';
import { StageConfig } from 'konva/lib/Stage';
import { Circle, CircleConfig } from 'konva/lib/shapes/Circle';
import { Line, LineConfig } from 'konva/lib/shapes/Line';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { DatalistInputComponent } from '../../../_shared/datalist-input/datalist-input.component';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { FileForPreview } from '../../../_shared/preview-thumbnails/preview-thumbnail.type';
import { ZaehlerApiService } from 'src/app/services/APIService/zaehler.api.service';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { ToastrService } from 'ngx-toastr';
import { ConfirmService } from 'src/app/services/confirm.service';
import { FormGroupOfType } from 'src/app/types/shared';
import { Shape } from 'konva/lib/Shape';
import { errorHandle } from 'src/app/misc/functions';
import { ifNotError } from 'src/app/misc/compositions';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';

@Component({
  selector: 'app-edit-skizzen-points',
  templateUrl: './edit-skizzen-points.html',
  styleUrl: './edit-skizzen-points.scss',
  imports: [
    StageComponent,
    CoreShapeComponent,
    ClrModalModule,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    DatalistInputComponent,
    ClrSelectModule,
    ClrFormsModule,
    ClrDatagridModule,
    ClrSpinnerModule,
    ClrTooltipModule,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditSkizzenPoints {
  private readonly api = inject(ZaehlerApiService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);
  private readonly confirm = inject(ConfirmService);

  readonly isModalOpen = model.required<boolean>();
  readonly skizze = input.required<FileForPreview>();
  readonly zWerteCodes = input<string[]>();

  readonly lineNode = viewChild<CoreShapeComponent>('line');
  readonly startNode = viewChild<CoreShapeComponent>('start');
  readonly endNode = viewChild<CoreShapeComponent>('end');

  protected readonly imageRs = resource({
    params: () => this.skizze()?.base64 || undefined,
    loader: ({ params: base64 }) => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const imageObj = new Image();
        imageObj.onload = () => resolve(imageObj);
        imageObj.onerror = err => reject(err);
        imageObj.src = `data:image/png;base64,${base64}`;
      });
    },
  });
  protected readonly configStage = computed<StageConfig>(() => {
    if (!this.imageRs.hasValue()) return {};
    return {
      width: this.imageRs.value().width,
      height: this.imageRs.value().height,
    };
  });

  protected readonly configImage = computed<ImageConfig>(() => {
    if (!this.imageRs.hasValue()) return null;
    const image = this.imageRs.value();
    return {
      image,
      x: 0,
      y: 0,
      width: image.width,
      height: image.height,
    };
  });

  protected readonly configCircle = computed<[CircleConfig, CircleConfig]>(() => {
    const { X1, Y1, X2, Y2 } = this.formValue();

    return [getCircle(X1, Y1), getCircle(X2, Y2)];

    function getCircle(x: number, y: number): CircleConfig {
      if (!x || !y) return null;
      return {
        x,
        y,
        radius: 4,
        fill: 'red',
        stroke: 'white',
        strokeWidth: 1,
        draggable: true,
      };
    }
  });

  protected readonly configLine = computed<LineConfig>(() => {
    const { X1, Y1, X2, Y2 } = this.formValue();
    if (!X1 || !Y1 || !X2 || !Y2) return null;

    return {
      points: [X1, Y1, X2, Y2],
      x: 0,
      y: 0,
      stroke: '#09e9fd94',
      strokeWidth: 25,
      lineCap: 'round',
      lineJoin: 'round',
      tension: 0,
      draggable: true,
    };
  });

  private readonly tableRs = resource({
    params: () => this.skizze()?.name || undefined,
    loader: ({ params: name }) => this.api.getSkizzenPoints(name),
  });

  protected readonly isLoading = this.tableRs.isLoading;
  protected readonly table = ifNotError(this.tableRs, TABLE_EMPTY());
  protected readonly selected = signal<SkizzenPoint[]>([]);
  protected readonly selectedID = computed(() => this.selected()[0]?.ID);

  protected readonly form = new FormGroupOfType<SkizzenPoint>({
    Code: new FormControl(null, Validators.required),
    X1: new FormControl(null, Validators.required),
    Y1: new FormControl(null, Validators.required),
    X2: new FormControl(null, Validators.required),
    Y2: new FormControl(null, Validators.required),
  });

  private readonly formValue = toSignal(this.form.valueChanges);
  protected readonly currentMode = signal<'start' | 'end' | 'none'>('none');
  protected readonly isDragging = signal(false);
  protected readonly canBeDragged = signal(false);

  constructor() {
    effect(() => {
      this.form.reset(this.selected()[0]);
      this.currentMode.set(this.selectedID() ? 'none' : 'start');
    });
  }

  protected onClick(event: NgKonvaEventObject<MouseEvent>) {
    if (this.currentMode() == 'none') return;

    const image = event.event.target as Shape<ImageConfig>;
    let { x, y } = image.getRelativePointerPosition();
    x = Math.round(x);
    y = Math.round(y);

    switch (this.currentMode()) {
      case 'start': {
        this.form.patchValue({ X1: x, Y1: y });
        if (!this.selectedID()) this.currentMode.set('end');
        break;
      }
      case 'end': {
        this.form.patchValue({ X2: x, Y2: y });
        if (!this.selectedID()) this.currentMode.set('none');
        break;
      }
    }
  }

  protected onCircleMoving(event: NgKonvaEventObject<MouseEvent>, mode: 'start' | 'end') {
    const circle = event.event.target as Circle;
    const lineNode = this.lineNode().getStage() as Line;

    let [x1, y1, x2, y2] = lineNode.points();
    const { x, y } = circle.position();
    if (mode == 'start') {
      x1 = x;
      y1 = y;
    } else {
      x2 = x;
      y2 = y;
    }

    lineNode.points([x1, y1, x2, y2]);
    circle.getLayer()?.batchDraw();
  }

  protected onCircleMoved(event: NgKonvaEventObject<MouseEvent>, mode: 'start' | 'end') {
    const circle = event.event.target as Circle;
    const x = Math.round(circle.x());
    const y = Math.round(circle.y());

    switch (mode) {
      case 'start': {
        this.form.patchValue({ X1: x, Y1: y });
        break;
      }
      case 'end': {
        this.form.patchValue({ X2: x, Y2: y });
        break;
      }
    }
  }

  protected onLineMoving(event: NgKonvaEventObject<MouseEvent>) {
    const line = event.event.target as Line;
    const offsetX = line.x();
    const offsetY = line.y();

    const start = this.startNode().getStage() as Circle;
    const end = this.endNode().getStage() as Circle;

    const { X1, Y1, X2, Y2 } = this.formValue();
    start.position({ x: X1 + offsetX, y: Y1 + offsetY });
    end.position({ x: X2 + offsetX, y: Y2 + offsetY });

    line.getLayer()?.batchDraw();
  }

  protected onLineMoved(event: NgKonvaEventObject<MouseEvent>) {
    const line = event.event.target as Line;
    const [x1, y1, x2, y2] = line.points();

    const offsetX = Math.round(line.x());
    const offsetY = Math.round(line.y());

    line.x(0);
    line.y(0);

    this.form.patchValue({
      X1: x1 + offsetX,
      Y1: y1 + offsetY,
      X2: x2 + offsetX,
      Y2: y2 + offsetY,
    });
  }

  protected async saveIfValid() {
    if (this.form.invalid) return;

    try {
      const ID = await this.api.setSkizzenPoint(this.skizze().name, this.normalizedPoint());

      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.tableRs.reload();
      this.selected.set([{ ID } as SkizzenPoint]);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  private normalizedPoint(): SkizzenPoint {
    let { Code, X1, Y1, X2, Y2 } = this.formValue();
    if (Math.abs(X1 - X2) <= 3) {
      const X = Math.round((X1 + X2) / 2);
      X1 = X2 = X;
    }

    return { ID: this.selectedID(), Code, X1, Y1, X2, Y2 };
  }

  protected async deletePoint() {
    try {
      const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie diese Zeile löschen wollen?');
      const ok = await this.confirm.danger(text);
      if (!ok) return;

      await this.api.deleteSkizzenPoint(this.selectedID());
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.tableRs.reload();
      this.selected.set([]);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }
}

export interface SkizzenPoint {
  ID?: number;
  /** @see ZählerWert.Code */
  Code: string;
  X1: number;
  Y1: number;
  X2: number;
  Y2: number;
}
